import React, { useRef, useEffect, useState } from 'react';

interface VerifyPageProps {
  email: string;
  onSubmit: (code: string) => void;
  error: string;
}

export const VerifyPage: React.FC<VerifyPageProps> = ({ email, onSubmit, error }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string>('');

  useEffect(() => {
    if (error) {
      setLocalError(error);
      setLoading(false);
      setHasSubmitted(false);
    }
  }, [error]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
    return () => {
      setLoading(false);
      setHasSubmitted(false);
      setLocalError('');
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      const currentVal = digits[index];
      if (currentVal === '' && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    let value = e.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (localError && value !== digits[index]) {
      setLocalError('');
      setHasSubmitted(false);
    }

    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  useEffect(() => {
    const allFilled = digits.every((d) => d !== '');
    if (allFilled && !localError && !hasSubmitted) {
      setHasSubmitted(true);
      setLoading(true);
      onSubmit(digits.join(''));
    }
  }, [digits, onSubmit, hasSubmitted, localError]);

  return (
    <div className="container neomorphic verify-page">
      <h2>E-postanızı doğrulayın</h2>
      <p>Lütfen {email} adresine gönderilen 6 haneli doğrulama kodunu girin.</p>
      <div className="otp-inputs">
        {Array(6)
          .fill(0)
          .map((_, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="otp-input"
              value={digits[index]}
              onFocus={(e) => e.target.select()}
              placeholder=""
              title={`Digit ${index + 1}`}
            />
          ))}
      </div>

      {loading && !localError && (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      )}
      {localError && <p className="error">{localError}</p>}
    </div>
  );
};
